/**
 * Aplica máscara e validação ao campo de data DD/MM/AAAA.
 * - Aceita apenas números
 * - Aplica máscara
 * - Não permite datas acima de hoje nem abaixo de 01/01/1900
 *
 * @param {string} value - valor digitado na tela
 * @param {Date} [hoje=new Date()] - data limite superior (default: hoje)
 * @returns {object} { masked: string, isValid: boolean }
 */
export function aplicarMascaraEValidarData(value, hoje = new Date()) {
  const raw = String(value).replace(/\D/g, '').slice(0, 8)

  // Aplica máscara
  let masked = raw
  if (masked.length >= 5) masked = masked.replace(/(\d{2})(\d{2})(\d+)/, '$1/$2/$3')
  else if (masked.length >= 3) masked = masked.replace(/(\d{2})(\d+)/, '$1/$2')
  // Faz validação
  let isValid = false
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(masked)) {
    const [dia, mes, ano] = masked.split('/').map(Number)
    const data = new Date(ano, mes - 1, dia)
    if (ano >= 1900 && data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia) {
      // Não pode ser futura
      data.setHours(0, 0, 0, 0)
      hoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
      if (data <= hoje) {
        isValid = true
      }
    }
  }

  return { masked, isValid }
}
/**
 * @param {2025-07-17T03:45:22} dateStr
 * OU
 * @param {2025-07-17T} dateStr
 * @returns "17/07/2025"
 */
export function formatDateBR(dateStr) {
  // Aceita '2025-07-17', '2025-07-17T17:20:20', etc
  if (!dateStr) return ''

  let date
  if (dateStr instanceof Date) {
    date = dateStr
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [ano, mes, dia] = dateStr.split('-').map(Number)
    date = new Date(ano, mes - 1, dia)
  } else {
    date = new Date(dateStr)
  }

  if (isNaN(date)) return ''
  const dia = String(date.getDate()).padStart(2, '0')
  const mes = String(date.getMonth() + 1).padStart(2, '0')
  const ano = date.getFullYear()
  return `${dia}/${mes}/${ano}`
}
/**
 * @param {2025-07-17T03:45:22} dateStr
 * @returns "17/07/2025 - 03:45:22"
 */
export function formatDateTimeBR(dateStr) {
  const date = new Date(dateStr)
  if (isNaN(date)) return ''
  const dia = String(date.getDate()).padStart(2, '0')
  const mes = String(date.getMonth() + 1).padStart(2, '0')
  const ano = date.getFullYear()
  const horas = String(date.getHours()).padStart(2, '0')
  const minutos = String(date.getMinutes()).padStart(2, '0')
  const segundos = String(date.getSeconds()).padStart(2, '0')
  return `${dia}/${mes}/${ano} - ${horas}:${minutos}:${segundos}`
}
/**
 * @param {string|Date} dateStr Ex: "2023-06-29T11:36:18"
 * @returns "29/06/2023 - 11:36"
 */
export function formatDateTimeBRWithoutSeconds(dateStr) {
  // dateStr: "2025-08-22T11:36:07.43"
  if (!dateStr) return ''

  if (typeof dateStr === 'string' && dateStr.length >= 16) {
    const ano = dateStr.substring(0, 4)
    const mes = dateStr.substring(5, 7)
    const dia = dateStr.substring(8, 10)
    const horas = dateStr.substring(11, 13)
    const minutos = dateStr.substring(14, 16)

    return `${dia}/${mes}/${ano} - ${horas}:${minutos}`
  }

  // Fallback para casos excepcionais (Date objects, etc)
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr)
  if (isNaN(date)) return ''

  const dia = String(date.getDate()).padStart(2, '0')
  const mes = String(date.getMonth() + 1).padStart(2, '0')
  const ano = date.getFullYear()
  const horas = String(date.getHours()).padStart(2, '0')
  const minutos = String(date.getMinutes()).padStart(2, '0')

  return `${dia}/${mes}/${ano} - ${horas}:${minutos}`
}
/**
 * Função para calcular idade a partir de uma data YYYY-MM-DD ou similar
 */
export function calculaIdade(dateString) {
  if (!dateString) return ''
  const hoje = new Date()
  const nascimento = new Date(dateString)
  let idade = hoje.getFullYear() - nascimento.getFullYear()
  const m = hoje.getMonth() - nascimento.getMonth()
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--
  }
  return idade
}
/**
 * Calcula a idade a partir de uma data no formato aaaammdd (ex: "20000101").
 * Retorna string vazia se a data for inválida.
 * @param {string} yyyymmdd
 * @returns {number|string}
 */
export function calculaIdadeAaaammdd(yyyymmdd) {
  if (!/^\d{8}$/.test(yyyymmdd)) return ''
  const ano = Number(yyyymmdd.substr(0, 4))
  const mes = Number(yyyymmdd.substr(4, 2))
  const dia = Number(yyyymmdd.substr(6, 2))
  const nascimento = new Date(ano, mes - 1, dia)
  if (
    isNaN(nascimento) ||
    nascimento.getFullYear() !== ano ||
    nascimento.getMonth() !== mes - 1 ||
    nascimento.getDate() !== dia
  )
    return ''
  const hoje = new Date()
  let idade = hoje.getFullYear() - ano
  if (hoje.getMonth() < mes - 1 || (hoje.getMonth() === mes - 1 && hoje.getDate() < dia)) {
    idade--
  }
  return idade
}

export function dateStringToDateBR(dateString) {
  if (!/^\d{8}$/.test(dateString)) return ''
  const year = dateString.substring(0, 4)
  const month = dateString.substring(4, 6)
  const day = dateString.substring(6, 8)
  return `${day}/${month}/${year}`
}

/**
 * Converte uma string "dd/mm/yyyy" para um inteiro no formato yyyymmdd
 * Exemplo: "17/07/2025" => 20250717
 */
export function dateBrToInt(dateBr) {
  if (!dateBr || typeof dateBr !== 'string') return null
  const [day, month, year] = dateBr.split('/')
  if (!day || !month || !year) return null
  return parseInt(`${year}${month}${day}`)
}

/**
 * aplica data padrão dd/mm/yyyy em string
 */
export function aplicarMascaraDeData(data) {
  const raw = String(data).replace(/\D/g, '').slice(0, 8);

  // Aplica máscara
  let dataComMascara = raw;
  if (dataComMascara.length >= 5)
    dataComMascara = dataComMascara.replace(/(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
  else if (dataComMascara.length >= 3) dataComMascara = dataComMascara.replace(/(\d{2})(\d+)/, '$1/$2');

  return dataComMascara;
}

/**
 * Verificar se a data de nascimento é válida
 * Data superior a 31/12/1899
 * Data que não é posterior a data atual
 * Data completa dd/mm/aaaa
 */

export function isDataValida(dataNascimento) {
  if (!dataNascimento || dataNascimento.length < 10) return false

  const [dia, mes, ano] = dataNascimento.split('/').map(Number)
  if (isNaN(dia) || isNaN(mes) || isNaN(ano) || dia < 1 || dia > 31 || mes < 1 || mes > 12 || ano < 1900) {
    return false
  }

  const data = new Date(ano, mes - 1, dia)
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return false
  }

  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  if (data > hoje) return false

  const dataMin = new Date(1900, 0, 1)
  if (data < dataMin) return false

  return true
}


/**
 * Monta a data do exame conforme regras:
 * - Se houver StudyDate e StudyTime, retorna "dd/mm/aaaa - hh:mm"
 * - Se faltar algum, usa created_at para montar "dd/mm/aaaa - hh:mm"
 * @param {string} studyDate - formato 'yyyymmdd' ou 'yyyy-mm-dd'
 * @param {string} studyTime - formato 'hhmmss' ou 'hh:mm:ss'
 * @param {string} createdAt - formato ISO ou similar
 * @returns {string}
 */

/**
 * Monta data do exame:
 * - Se studyDate e studyTime existirem, retorna "dd/mm/aaaa - hh:mm"
 * - Se um deles faltar, retorna "dd/mm/aaaa - hh:mm" usando created_at
 */
export function montaDataExame(studyDate, studyTime, createdAt) {
  if (!studyDate || !studyTime) {
    return createdAt ? formatDateTimeBRWithoutSeconds(createdAt) : ''
  }

  const dia = studyDate.substring(8, 10)
  const mes = studyDate.substring(5, 7)
  const ano = studyDate.substring(0, 4)

  const hora = studyTime.substring(11, 16)

  return `${dia}/${mes}/${ano} - ${hora}`
}
/**
 * Recebe data 'dd/mm/yyyy' retorna yyyy-mm-dd
 */

export function formataDataYYYYMMDD(data) {
  const [dia, mes, ano] = data.split('/')
  return `${ano}-${mes}-${dia}`
}
